import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind CSS classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format distance in kilometers
 */
export function formatDistance(km: number): string {
  if (km < 1) {
    return `${Math.round(km * 1000)}m away`;
  }
  if (km < 10) {
    return `${km.toFixed(1)}km away`;
  }
  return `${Math.round(km)}km away`;
}

/**
 * Calculate age from birth date
 */
export function calculateAge(birthDate: string | Date): number {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return then.toLocaleDateString();
  }
}

/**
 * Format time for chat messages
 * Handles UTC timestamps from server by ensuring proper timezone conversion
 */
export function formatMessageTime(date: string | Date): string {
  // Ensure UTC string is parsed correctly
  let messageDate: Date;
  if (typeof date === 'string') {
    // If string doesn't end with 'Z', it's treated as local time by default
    // Force UTC parsing by adding 'Z' if missing (server sends UTC without 'Z')
    const dateStr = date.endsWith('Z') ? date : `${date}Z`;
    messageDate = new Date(dateStr);
  } else {
    messageDate = date;
  }
  
  const now = new Date();
  const isToday = messageDate.toDateString() === now.toDateString();
  
  if (isToday) {
    return messageDate.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  }
  
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = messageDate.toDateString() === yesterday.toDateString();
  
  if (isYesterday) {
    return 'Yesterday';
  }
  
  const diffDays = Math.floor((now.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    return messageDate.toLocaleDateString([], { weekday: 'short' });
  }
  
  return messageDate.toLocaleDateString([], { 
    month: 'short', 
    day: 'numeric' 
  });
}

/**
 * Debounce function calls
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate?: boolean
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    
    const callNow = immediate && !timeout;
    
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    
    if (callNow) func(...args);
  };
}

/**
 * Throttle function calls
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Generate random ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/**
 * Sleep/delay utility
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Check if device is mobile
 */
export function isMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Check if device supports touch
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * Get device pixel ratio
 */
export function getPixelRatio(): number {
  if (typeof window === 'undefined') return 1;
  return window.devicePixelRatio || 1;
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate URL format
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const result = document.execCommand('copy');
      textArea.remove();
      return result;
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

/**
 * Download file from URL
 */
export function downloadFile(url: string, filename?: string): void {
  const link = document.createElement('a');
  link.href = url;
  if (filename) {
    link.download = filename;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Convert file to base64
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
}

/**
 * Compress image file
 */
export function compressImage(
  file: File,
  maxWidth: number = 800,
  maxHeight: number = 600,
  quality: number = 0.8
): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx?.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        file.type,
        quality
      );
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Process and optimize image file for upload
 * - Converts HEIC to JPEG (for iPhone compatibility)
 * - Compresses images to reasonable size
 * - Validates file types
 */
export async function processImageFile(
  file: File,
  maxWidth: number = 1920,
  maxHeight: number = 1920,
  quality: number = 0.85
): Promise<File> {
  try {
    console.log('📸 Processing image:', {
      name: file.name,
      type: file.type,
      size: formatFileSize(file.size),
    });

    // Check if file is HEIC/HEIF format (common on iPhone)
    const isHEIC = file.type === 'image/heic' || 
                   file.type === 'image/heif' || 
                   file.name.toLowerCase().endsWith('.heic') ||
                   file.name.toLowerCase().endsWith('.heif');

    let processedFile = file;

    // Convert HEIC to JPEG
    if (isHEIC) {
      console.log('🔄 Converting HEIC to JPEG...');
      try {
        // Dynamically import heic2any
        const heic2any = (await import('heic2any')).default;
        
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.9,
        });

        // heic2any can return Blob or Blob[], handle both cases
        const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        
        // Create new File from converted blob
        const newFileName = file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg');
        processedFile = new File([blob], newFileName, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        
        console.log('✅ HEIC converted to JPEG');
      } catch (error) {
        console.error('❌ HEIC conversion failed:', error);
        throw new Error('Не удалось конвертировать фото. Попробуйте выбрать другое изображение.');
      }
    }

    // Validate file type after potential conversion
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(processedFile.type)) {
      throw new Error(`Неподдерживаемый формат изображения: ${processedFile.type}. Используйте JPEG, PNG или WebP.`);
    }

    // Compress image if it's too large
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (processedFile.size > maxSize || processedFile.size > 500 * 1024) {
      console.log('🗜️ Compressing image...');
      processedFile = await compressImage(processedFile, maxWidth, maxHeight, quality);
      console.log('✅ Image compressed:', formatFileSize(processedFile.size));
    }

    console.log('✅ Image processing complete:', {
      name: processedFile.name,
      type: processedFile.type,
      size: formatFileSize(processedFile.size),
    });

    return processedFile;
  } catch (error: any) {
    console.error('❌ Image processing failed:', error);
    throw error;
  }
}

/**
 * Calculate tech stack compatibility
 */
export function calculateTechMatch(userTech: string[], otherTech: string[]): number {
  if (userTech.length === 0 || otherTech.length === 0) return 0;
  
  const intersection = userTech.filter(tech => otherTech.includes(tech));
  const union = [...new Set([...userTech, ...otherTech])];
  
  return Math.round((intersection.length / union.length) * 100);
}

/**
 * Generate AI compatibility score (mock)
 */
export function generateAIMatch(
  userProfile: any,
  otherProfile: any
): number {
  // Check if profiles exist
  if (!userProfile || !otherProfile) return 50;
  
  // Mock AI calculation based on various factors
  let score = 50; // base score
  
  // Tech stack match
  const techMatch = calculateTechMatch(userProfile.techStack || [], otherProfile.techStack || []);
  score += (techMatch / 100) * 30;
  
  // Age proximity
  const ageDiff = Math.abs((userProfile.age || 25) - (otherProfile.age || 25));
  if (ageDiff <= 2) score += 10;
  else if (ageDiff <= 5) score += 5;
  else if (ageDiff > 10) score -= 10;
  
  // Location proximity (if available)
  if (userProfile.distanceKm && userProfile.distanceKm <= 10) score += 10;
  else if (userProfile.distanceKm && userProfile.distanceKm <= 50) score += 5;
  
  // Lifestyle match
  const lifestyleMatch = userProfile.lifestyle?.filter((item: string) => 
    otherProfile.lifestyle?.includes(item)
  ).length || 0;
  score += lifestyleMatch * 2;
  
  // Random factor for variety
  score += (Math.random() - 0.5) * 20;
  
  return Math.max(10, Math.min(99, Math.round(score)));
}

/**
 * Local storage helpers with error handling
 */
export const storage = {
  get: <T = any>(key: string, defaultValue?: T): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue || null;
    } catch (error) {
      console.error(`Error reading from localStorage:`, error);
      return defaultValue || null;
    }
  },
  
  set: (key: string, value: any): boolean => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error(`Error writing to localStorage:`, error);
      return false;
    }
  },
  
  remove: (key: string): boolean => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing from localStorage:`, error);
      return false;
    }
  },
  
  clear: (): boolean => {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error(`Error clearing localStorage:`, error);
      return false;
    }
  },
};

/**
 * Array utilities
 */
export const arrayUtils = {
  shuffle: <T>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  },
  
  chunk: <T>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  },
  
  unique: <T>(array: T[]): T[] => [...new Set(array)],
  
  groupBy: <T>(array: T[], key: keyof T): Record<string, T[]> => {
    return array.reduce((groups, item) => {
      const group = String(item[key]);
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(item);
      return groups;
    }, {} as Record<string, T[]>);
  },
};

/**
 * String utilities
 */
export const stringUtils = {
  capitalize: (str: string): string => 
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase(),
    
  truncate: (str: string, length: number, suffix: string = '...'): string =>
    str.length <= length ? str : str.substring(0, length) + suffix,
    
  slug: (str: string): string =>
    str
      .toLowerCase()
      .replace(/[^a-zA-Z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, ''),
      
  camelCase: (str: string): string =>
    str.replace(/[-_\s]+(.)?/g, (_, c) => c ? c.toUpperCase() : ''),
    
  kebabCase: (str: string): string =>
    str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase(),
};

export * from './telegram';
export * from './mappers';
export * from './badges';
export * from './linkify';
